/**
 * Workspace-level summaries: campaign metrics, channel performance and web
 * analytics. Lifted out of mediaIntelligenceService unchanged.
 */

import { isSupabaseConfigured } from '@/config';
import { DEMO_MODE } from '@/config/demoConfig';
import { measuredRoas } from '@/components/mmm/unmeasured';
import { logError } from './logger';
import { periodToRange, readCampaignSummary, type Scope } from './mediaIntelligenceShared';
import type { CampaignMetricsData, ChannelPerformance } from './mediaIntelligenceTypes';
import { EMPTY_CAMPAIGN_METRICS } from './mediaIntelligenceEntities';
import { getMockCampaignMetrics, getMockChannelPerformance } from './mediaIntelligenceMocks';

// ============================================================================
// CAMPAIGN METRICS
// ============================================================================

export const fetchCampaignMetrics = async (scope: Scope, periodType = 'month'): Promise<CampaignMetricsData> => {

  if (!isSupabaseConfigured()) {
    return DEMO_MODE ? getMockCampaignMetrics() : EMPTY_CAMPAIGN_METRICS;
  }

  try {
    // Date-ranged rollup: get_campaign_summary mirrors v_campaign_summary but
    // filtered to the selected period (the view is all-time).
    const { start, end } = periodToRange(periodType);
    const { rows: data, error } = await readCampaignSummary(scope, start, end, 'fetchCampaignMetrics');

    if (error) {
      logError('MediaIntelligenceService', 'Error fetching campaign metrics', error);
      // Demo mode still answers with its fixture. Outside it a refusal is
      // reported as a refusal: the empty/zero value this used to return is
      // the same one that means "nothing ran" — a claim about the account.
      if (DEMO_MODE) return getMockCampaignMetrics();
      throw new Error(`Failed reading campaign metrics: ${(error as { message?: string })?.message ?? 'unknown error'}`);
    }

    if (data.length === 0) {
      return DEMO_MODE ? getMockCampaignMetrics() : EMPTY_CAMPAIGN_METRICS;
    }

    // Aggregate metrics across all campaigns. Google Analytics is web-analytics, not
    // an ad platform — its site-wide event/conversion counts dwarf ad data, so it's
    // shown in its own Web Analytics card and excluded from the blended ad totals.
    const rows = data.filter((r) => r.platform !== 'analytics');
    const totalImpressions = rows.reduce((sum, row) => sum + (row.total_impressions || 0), 0);
    const totalClicks = rows.reduce((sum, row) => sum + (row.total_clicks || 0), 0);
    const totalCost = rows.reduce((sum, row) => sum + Number(row.total_spend || 0), 0);
    const totalPurchases = rows.reduce((sum, row) => sum + (row.total_conversions || 0), 0);
    // Same reduce over the same column, kept only because `leads` is still on
    // CampaignMetricsData and 90 call sites read it. There is no signup metric
    // in this database — nothing on screen or in an export presents this as a
    // lead count any more, because it is the conversion count twice.
    const totalSignups = totalPurchases;
    const totalValue = rows.reduce((sum, row) => sum + Number(row.total_value || 0), 0);

    // Calculate derived metrics
    // Guarded on the DENOMINATOR. It used to test totalPurchases, so a
    // view-through conversion — conversions with no clicks, which Facebook and
    // Google both report — produced Infinity and rendered "Infinity%".
    const conversionRate = totalClicks > 0 ? ((totalPurchases / totalClicks) * 100) : 0;
    const cpl = totalPurchases > 0 ? (totalCost / totalPurchases) : 0;
    const roas = measuredRoas(totalValue, totalCost);

    // Null, not 0. Growth needs the previous period and this query fetches one
    // window, so nothing here computes it — and 0 is a MEASUREMENT in this
    // codebase, the one that says a metric did not move. Reported as 0 these
    // reached the dashboard hero as a neutral "no change" chip and the AI prompt
    // as a figure the model was told to reason from.
    const impressionsGrowth = null;
    const ctrGrowth = null;
    const leadsGrowth = null;
    const conversionsGrowth = null;
    const cplChange = null;

    return {
      impressions: totalImpressions,
      impressionsGrowth,
      // Guarded on the DENOMINATOR. It used to test totalClicks, so clicks
      // with zero impressions produced Infinity, and the funnel turned that
      // into a NaN stage value downstream.
      ctr: totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100) : 0,
      ctrGrowth,
      clicks: totalClicks,
      cpl,
      leads: totalSignups,
      leadsGrowth,
      conversions: totalPurchases,
      conversionsGrowth,
      cplChange,
      conversionRate,
      monthlyRevenue: totalValue,
      monthlySpend: totalCost,
      roas,
    };
  } catch (error) {
    logError('MediaIntelligenceService', 'Error fetching campaign metrics', error);
    // Demo mode still answers with its fixture. Outside it a refusal is
    // reported as a refusal: the empty/zero value this used to return is
    // the same one that means "nothing ran" — a claim about the account.
    if (DEMO_MODE) return getMockCampaignMetrics();
    throw error;
  }
};

// ============================================================================
// CHANNEL PERFORMANCE
// ============================================================================

/**
 * How each platform is named and iconised on screen.
 *
 * Exported because the channel filter draws a checkbox per platform and must use
 * these exact labels: a second copy of "Facebook 📘 / Google Ads 🔍 / …" beside
 * this one is how a filter ends up offering a channel the data calls something
 * else. `analytics` is listed because this map also labels the Web Analytics
 * card — it is NOT an ad channel, is skipped by the aggregation below, and is
 * absent from `AD_CHANNEL_IDS` on purpose.
 */
export const PLATFORM_DISPLAY: Record<string, { name: string; icon: string }> = {
  facebook: { name: 'Facebook', icon: '📘' },
  google: { name: 'Google Ads', icon: '🔍' },
  tiktok: { name: 'TikTok', icon: '🎵' },
  line: { name: 'LINE Ads', icon: '💬' },
  analytics: { name: 'Google Analytics', icon: '📊' },
};

export const fetchChannelPerformance = async (scope: Scope, periodType = 'month'): Promise<ChannelPerformance[]> => {

  if (!isSupabaseConfigured()) {
    return DEMO_MODE ? getMockChannelPerformance() : [];
  }

  try {
    // Date-ranged rollup by campaign (same period as everything else on the page).
    const { start, end } = periodToRange(periodType);
    const { rows: data, error } = await readCampaignSummary(scope, start, end, 'fetchChannelPerformance');

    if (error) {
      logError('MediaIntelligenceService', 'Error fetching channel performance', error);
      // Demo mode still answers with its fixture. Outside it a refusal is
      // reported as a refusal: the empty/zero value this used to return is
      // the same one that means "nothing ran" — a claim about the account.
      if (DEMO_MODE) return getMockChannelPerformance();
      throw new Error(`Failed reading channel performance: ${(error as { message?: string })?.message ?? 'unknown error'}`);
    }

    if (data.length === 0) {
      return DEMO_MODE ? getMockChannelPerformance() : [];
    }

    // Aggregate by platform
    const platformMap = new Map<string, {
      impressions: number;
      clicks: number;
      cost: number;
      conversions: number;
      value: number;
      reach: number;
    }>();

    data.forEach((row) => {
      const platform = row.platform;
      // GA -> its own Web Analytics card (fetchWebAnalytics), not an ad channel.
      if (platform === 'analytics') return;
      if (!platformMap.has(platform)) {
        platformMap.set(platform, {
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          value: 0,
          reach: 0,
        });
      }
      const metrics = platformMap.get(platform)!;
      metrics.impressions += row.total_impressions || 0;
      metrics.reach += Number(row.total_reach || 0);
      metrics.clicks += row.total_clicks || 0;
      metrics.cost += Number(row.total_spend || 0);
      metrics.conversions += row.total_conversions || 0;
      metrics.value += Number(row.total_value || 0);
    });

    // Convert to ChannelPerformance format
    const results: ChannelPerformance[] = [];
    platformMap.forEach((metrics, platform) => {
      const config = PLATFORM_DISPLAY[platform] || { name: platform, icon: '📱' };
      const conversions = metrics.conversions;
      const cost = metrics.cost;

      results.push({
        id: platform,
        key: platform,
        name: config.name,
        icon: config.icon,
        impressions: metrics.impressions,
        clicks: metrics.clicks,
        // Real reach since migration 037 exposed total_reach. This was
        // `metrics.impressions` with a "reach proxy" comment — impressions count
        // every view, reach counts people, and the two differ by a factor of
        // seven on this workspace.
        reach: metrics.reach,
        ctr: metrics.impressions > 0 ? ((metrics.clicks / metrics.impressions) * 100) : 0,
        conversions,
        cost,
        revenue: metrics.value,
        cpc: metrics.clicks > 0 ? (cost / metrics.clicks) : 0,
        conversionRate: metrics.clicks > 0 ? ((conversions / metrics.clicks) * 100) : 0,
        // null, not 0: revenue of 0 means the channel never sent conversion value
        // far more often than it means it earned nothing, and the two must not
        // render the same. Downstream shows null as "—".
        roas: measuredRoas(metrics.value, cost),
        campaigns: [],
        adSets: [],
      });
    });

    return results;
  } catch (error) {
    logError('MediaIntelligenceService', 'Error fetching channel performance', error);
    // Demo mode still answers with its fixture. Outside it a refusal is
    // reported as a refusal: the empty/zero value this used to return is
    // the same one that means "nothing ran" — a claim about the account.
    if (DEMO_MODE) return getMockChannelPerformance();
    throw error;
  }
};

/** Google Analytics summary for the standalone Web Analytics card. */
export interface WebAnalyticsSummary {
  propertyName: string;
  events: number;       // GA eventCount (carried as total_clicks in the rollup)
  conversions: number;  // GA key events / conversions
  revenue: number;      // GA totalRevenue (carried as total_value)
}

/**
 * Aggregate the Google Analytics (platform 'analytics') rows from the scoped
 * campaign rollup into a standalone summary. GA is excluded from the ad totals and
 * channel breakdown (its site-wide counts dwarf ad data); this powers a separate
 * Web Analytics card. Returns null when no GA data is synced for the period, so the
 * card only renders when there is something to show.
 */
export const fetchWebAnalytics = async (
  scope: Scope,
  periodType = 'month',
): Promise<WebAnalyticsSummary | null> => {
  if (!isSupabaseConfigured()) return null;

  try {
    const { start, end } = periodToRange(periodType);
    const { rows: data, error } = await readCampaignSummary(scope, start, end, 'fetchWebAnalytics');
    // null is this function's answer for "no GA data synced", and the card only
    // renders when it is non-null — so null cannot also be its answer for a read
    // that failed, or a refusal silently removes the card and reads as a
    // property that reports nothing.
    if (error) {
      throw new Error(
        `Failed reading web analytics: ${(error as { message?: string })?.message ?? 'unknown error'}`,
      );
    }
    const gaRows = data.filter((r) => r.platform === 'analytics');
    if (gaRows.length === 0) return null;

    return {
      propertyName: gaRows[0].campaign_name,
      events: gaRows.reduce((s, r) => s + (r.total_clicks || 0), 0),
      conversions: gaRows.reduce((s, r) => s + (r.total_conversions || 0), 0),
      revenue: gaRows.reduce((s, r) => s + Number(r.total_value || 0), 0),
    };
  } catch (error) {
    logError('MediaIntelligenceService', 'Error fetching web analytics', error);
    throw error;
  }
};
